import React, { useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Container, Row, Spinner, Table } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import { formatDate } from '../utils/dateFormat';
import HelpIcon from './HelpIcon';
import HelpPane from './HelpPane';
import { getHelpContent } from './helpContent';

const money = (amount, currency) => `${currency || ''} ${Number(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`.trim();

const SUBSCRIPTION = { TRIAL: ['info', 'On trial'], ACTIVE: ['success', 'Paying'], EXPIRED: ['secondary', 'Lapsed'] };

/**
 * An affiliate's own screen: the link and code to share, the organizations that signed up with it (their name and whether they are paying,
 * nothing else of theirs), and what has been earned and paid out.
 */
function Affiliate() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [showHelp, setShowHelp] = useState(false);
  const help = getHelpContent('partner-affiliate');

  useEffect(() => {
    axios.get('/api/affiliate/me')
      .then((r) => setData(r.data))
      .catch((e) => setError(typeof e.response?.data === 'string' ? e.response.data : 'Could not load your affiliate details'));
  }, []);

  const copy = async (text, what) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${what} copied`);
    } catch (e) {
      toast.error('Could not copy; select it and copy by hand');
    }
  };

  if (error) return <Container className="mt-4"><Alert variant="danger">{error}</Alert></Container>;
  if (!data) return <div className="d-flex justify-content-center py-5"><Spinner animation="border" /></div>;

  const t = data.terms;
  const whatsapp = `https://wa.me/?text=${encodeURIComponent(`iBigDo is an ERP for manufacturing, processing and trading businesses: purchase, stock, production, sales, accounts and GST in one place. Sign up with my code ${data.code} here: ${data.link}`)}`;

  return (
    <Container className="mt-3" data-testid="affiliate-page">
      <div className="d-flex align-items-center mb-3">
        <h5 className="mb-0">Your referrals{help && <HelpIcon onClick={() => setShowHelp(true)} />}</h5>
      </div>

      {!data.programEnabled && (
        <Alert variant="warning" className="py-2">The affiliate program is paused, so new sign-ups cannot use your code for now. What you have earned is unchanged.</Alert>
      )}

      <Row>
        <Col md={7}>
          <Card className="mb-3">
            <Card.Header><strong>Your link and code</strong></Card.Header>
            <Card.Body>
              <p className="small text-muted">
                Give this link, or the code, to a business that could use iBigDo. When they sign up with it, they are credited to you
                from that day.
              </p>
              <div className="d-flex gap-2 align-items-center mb-2 flex-wrap">
                <code className="user-select-all" data-testid="affiliate-link">{data.link}</code>
                <Button size="sm" variant="outline-secondary" onClick={() => copy(data.link, 'Link')}>Copy link</Button>
              </div>
              <div className="d-flex gap-2 align-items-center flex-wrap">
                <span>Code: <strong className="user-select-all" data-testid="affiliate-code">{data.code}</strong></span>
                <Button size="sm" variant="outline-secondary" onClick={() => copy(data.code, 'Code')}>Copy code</Button>
                <a className="btn btn-sm btn-outline-success" href={whatsapp} target="_blank" rel="noopener noreferrer">
                  <i className="bi bi-whatsapp me-1" />Share on WhatsApp
                </a>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={5}>
          <Card className="mb-3">
            <Card.Header><strong>What you earn</strong></Card.Header>
            <Card.Body className="small">
              <div><strong>{t.initialRatePercent}%</strong> of what a referred business pays, for the first {t.initialPeriodMonths} months.</div>
              <div><strong>{t.trailingRatePercent}%</strong> after that{t.capMonths ? `, until ${t.capMonths} months from when they signed up` : ', for as long as they pay'}.</div>
              <div className="text-muted mt-2">It accrues each time a payment is recorded. iBigDo pays it out to you separately.</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col xs={6} md={3}><Card><Card.Body><div className="text-muted small">Waiting to be paid</div><div className="fs-5" data-testid="affiliate-accrued">{money(data.accrued, data.currency)}</div></Card.Body></Card></Col>
        <Col xs={6} md={3}><Card><Card.Body><div className="text-muted small">Paid to you</div><div className="fs-5" data-testid="affiliate-paid">{money(data.paid, data.currency)}</div></Card.Body></Card></Col>
        <Col xs={6} md={3}><Card><Card.Body><div className="text-muted small">Businesses referred</div><div className="fs-5">{data.referrals.length}</div></Card.Body></Card></Col>
        <Col xs={6} md={3}><Card><Card.Body><div className="text-muted small">Paying now</div><div className="fs-5">{data.referrals.filter((r) => r.subscription === 'ACTIVE' && r.referral === 'ACTIVE').length}</div></Card.Body></Card></Col>
      </Row>

      <Card className="mb-3">
        <Card.Header><strong>Businesses you referred</strong></Card.Header>
        <Card.Body className="p-0">
          <Table responsive className="mb-0">
            <thead><tr><th>Business</th><th>Signed up</th><th>Status</th><th>Plan</th><th className="text-end">You earned</th></tr></thead>
            <tbody>
              {data.referrals.length === 0 ? (
                <tr><td colSpan="5" className="text-center text-muted py-3">Nobody yet. Share your link to get started.</td></tr>
              ) : data.referrals.map((r, i) => {
                const [variant, label] = SUBSCRIPTION[r.subscription] || ['secondary', r.subscription || '-'];
                return (
                  <tr key={i}>
                    <td>{r.organization}{r.referral === 'ENDED' && <Badge bg="secondary" className="ms-2">Ended</Badge>}</td>
                    <td>{formatDate(r.since)}</td>
                    <td><Badge bg={variant}>{label}</Badge></td>
                    <td>{r.plan || '-'}</td>
                    <td className="text-end">{money(r.earned, data.currency)}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Header><strong>Your earnings</strong></Card.Header>
        <Card.Body className="p-0">
          <Table responsive className="mb-0">
            <thead><tr><th>Date</th><th>Business</th><th>Rate</th><th className="text-end">Amount</th><th>Status</th></tr></thead>
            <tbody>
              {data.commissions.length === 0 ? (
                <tr><td colSpan="5" className="text-center text-muted py-3">Nothing earned yet. It appears here after a referred business pays.</td></tr>
              ) : data.commissions.map((c, i) => (
                <tr key={i}>
                  <td className="text-nowrap">{formatDate(c.date)}</td>
                  <td>{c.organization}</td>
                  <td>{c.ratePercent}%</td>
                  <td className={`text-end ${c.reversal ? 'text-danger' : ''}`}>{money(c.amount, c.currency)}</td>
                  <td>
                    {c.reversal
                      ? <><Badge bg="danger">Refunded</Badge> <span className="small text-muted">{c.clawback && c.status !== 'PAID' ? 'taken off your next payout' : c.status === 'PAID' ? 'settled' : 'not payable'}</span></>
                      : c.status === 'PAID' ? <Badge bg="success">Paid {c.paidAt ? formatDate(c.paidAt) : ''}</Badge> : <Badge bg="secondary">Waiting</Badge>}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {help && <HelpPane show={showHelp} onHide={() => setShowHelp(false)} title={help.title}>{help.content}</HelpPane>}
    </Container>
  );
}

export default Affiliate;
